/**
 * Connections wording (Prototype v18, backlog b573085f), kept out of the
 * screen so it can be tested without the router.
 */

const shortDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** "localhost:8080" from "http://localhost:8080/", or the text as given. */
export function hostOf(url?: string | null) {
  const raw = (url || '').trim();
  if (!raw) return '';
  try {
    return new URL(/^[a-z]+:\/\//i.test(raw) ? raw : `http://${raw}`).host || raw;
  } catch {
    return raw;
  }
}

/**
 * An address only the computer it runs on can reach: localhost, a loopback
 * or private-network IP, or a .local / .test / .localhost name. Readers can't
 * reach a WordPress site there, so links to its posts open for no one else.
 */
export function isTestAddress(url?: string | null) {
  const host = hostOf(url)
    .replace(/:\d+$/, '')
    .replace(/^\[|\]$/g, '')
    .toLowerCase();
  if (!host) return false;
  if (host === 'localhost' || host === '::1' || host === '0.0.0.0') return true;
  if (/\.(localhost|local|test|internal)$/.test(host)) return true;
  const ip = host.match(/^(\d+)\.(\d+)\.\d+\.\d+$/);
  if (!ip) return false;
  const [a, b] = [Number(ip[1]), Number(ip[2])];
  return a === 127 || a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31);
}

/** "Paused Sep 2": the banner above the list says what pausing means, once. */
export function sourcePausedStatus(pausedAt?: string | null) {
  const when = shortDate(pausedAt);
  return `Paused${when ? ` ${when}` : ''}`;
}

/**
 * A paired WordPress site sends its posts into tinycms (the plugin pushes on
 * publish and edit). tinycms never publishes into WordPress (owner decision,
 * 2026-10-27), so these lines describe that direction only.
 */
export const SITE_RULE = 'Its posts come into tinycms';

/** "Paused Sep 2. Its new posts don't come in; what's already here stays." */
export function sitePausedNote(pausedAt?: string | null) {
  const when = shortDate(pausedAt);
  return `Paused${when ? ` ${when}` : ''}. Its new posts don’t come in; what’s already here stays.`;
}

/** "Paired Jul 19 · Its posts come into tinycms · last heard from Jul 22". */
export function siteMeta(createdAt?: string | null, lastUsedAt?: string | null) {
  const paired = shortDate(createdAt);
  const heard = shortDate(lastUsedAt);
  return [
    paired ? `Paired ${paired}` : 'Paired',
    SITE_RULE,
    heard ? `last heard from ${heard}` : 'not heard from yet',
  ].join(' · ');
}

/** The toast after Resume / Pause on a WordPress site. */
export function siteToggleToast(
  site: { name: string; url?: string | null },
  nowOn: boolean
): string {
  const label = site.name || hostOf(site.url);
  return nowOn
    ? `${label} resumed. Its new posts come in again.`
    : `${label} paused. Its new posts don’t come in until you resume.`;
}

/** On first, then by name; the screen lists sources in this order. */
export function onFirst<T extends { isActive: boolean; name: string }>(list: T[]): T[] {
  return [...list].sort(
    (a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name)
  );
}
